import { createFileRoute, Link } from "@tanstack/react-router";
import { PHYSICIAN_REF } from "@/lib/medical-schema";
import { SOCIAL_PREVIEW_IMAGE, SOCIAL_PREVIEW_ALT } from "@/lib/social-meta";
import { Flower } from "@/components/site/Flower";
import { EditableText } from "@/components/site/EditableText";
import { PageSection } from "@/components/site/PageSection";
import conditionsOfRegistration from "@/assets/Conditions_of_Registration.pdf.asset.json";
import patientRegistration from "@/assets/Patient_Registration.pdf.asset.json";
import recordsRelease from "@/assets/Records_Release.pdf.asset.json";
import telemedicineConsent from "@/assets/Consent_for_Telemedicine_Services.pdf.asset.json";
import { FileText } from "lucide-react";
import { schemaDateFields } from "@/lib/schema-dates";

export const Route = createFileRoute("/resources/forms")({
  head: () => ({
    meta: [
      { title: "Patient Forms | Virginia Bariatric" },
      {
        name: "description",
        content:
          "Download and complete patient intake, medical history, and consent forms for Virginia Bariatric Surgery.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Patient Forms | Virginia Bariatric" },
        { property: "og:description", content: "Download and complete patient intake, medical history, and consent forms for Virginia Bariatric Surgery." },
        { property: "og:type", content: "website" },
        { property: "og:url", content: "https://virginiabariatric.com/resources/forms" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Patient Forms | Virginia Bariatric" },
        { name: "twitter:description", content: "Download and complete patient intake, medical history, and consent forms for Virginia Bariatric Surgery." },
        { property: "og:image", content: SOCIAL_PREVIEW_IMAGE },
        { property: "og:image:alt", content: SOCIAL_PREVIEW_ALT },
        { name: "twitter:image", content: SOCIAL_PREVIEW_IMAGE },
        { name: "twitter:image:alt", content: SOCIAL_PREVIEW_ALT },
      ] : []),
    ],
    links: [{ rel: "canonical", href: "https://virginiabariatric.com/resources/forms" }],
    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          ...schemaDateFields("/resources/forms"),
          reviewedBy: PHYSICIAN_REF,
          "@id": "https://virginiabariatric.com/resources/forms#webpage",
          url: "https://virginiabariatric.com/resources/forms",
          name: "New Patient Forms",
          description:
            "Downloadable patient intake, registration, records release, and telemedicine consent forms for Virginia Bariatric Surgery.",
          inLanguage: "en-US",
          isPartOf: { "@id": "https://virginiabariatric.com/#website" },
          about: { "@type": "MedicalCondition", name: "Obesity" },
          audience: {
            "@type": "MedicalAudience",
            audienceType: "New bariatric surgery patients",
          },
          specialty: "Bariatric",
        }),
      },
    ] : [],
  }),
  component: FormsPage,
});

const forms = [
  {
    title: "Conditions of Registration",
    description: "Office policies, financial responsibilities, and registration details.",
    asset: conditionsOfRegistration,
  },
  {
    title: "Patient Registration",
    description: "New patient registration form with demographic and contact information.",
    asset: patientRegistration,
  },
  {
    title: "Records Release",
    description: "Authorization to release medical records to or from another provider.",
    asset: recordsRelease,
  },
  {
    title: "Consent for Telemedicine Services",
    description: "Consent form for receiving telemedicine/telehealth services.",
    asset: telemedicineConsent,
  },
];

function FormsPage() {
  return (
    <>
      <PageSection className="relative pt-4 md:pt-6 pb-3 md:pb-4">
        <Flower className="hidden md:block right-2 top-6 w-56 lg:w-64" />
        <div className="text-center max-w-4xl mx-auto">
          <EditableText
            as="h1"
            contentKey="forms.hero.title"
            defaultValue="New Patient Forms"
            className="text-4xl md:text-5xl font-serif"
          />
        </div>
      </PageSection>

      <PageSection fullWidth maxWidth="4xl" className="relative z-10 bg-brand-soft py-3 md:py-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {forms.map((form) => (
            <a
              key={form.asset.asset_id}
              href={form.asset.url}
              download={form.asset.original_filename}
              type="application/pdf"
              className="group rounded-2xl border border-border bg-card py-3 px-5 transition hover:-translate-y-1 hover:border-brand hover:shadow-xl"
            >
              <FileText className="h-6 w-6 text-brand-mid" />
              <h3 className="mt-2 text-lg text-brand-mid">{form.title}</h3>
              <p className="mt-0.5 text-sm text-muted-foreground leading-[1.6]">{form.description}</p>
              <span className="mt-2 inline-block text-sm font-medium text-brand-mid group-hover:translate-x-1 transition">
                Download PDF →
              </span>
            </a>
          ))}
        </div>
      </PageSection>

      <PageSection className="pt-4 pb-8 text-center">
        <EditableText
          as="p"
          contentKey="forms.body"
          defaultValue="Need help with a form? Contact our office and we’ll send it to you."
          className="text-foreground/80 leading-[1.6]"
        />
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to="/contact"
            className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Contact Us
          </Link>
          <Link
            to="/resources"
            className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            Back to Resources
          </Link>
        </div>
      </PageSection>
    </>
  );
}
